import type { NetworkId } from "@alch/dx-entities";
import type React from "react";
import type { Control, FieldPathByValue, FieldValues } from "react-hook-form";
import { SelectField } from "../../components/ui/index.js";
import type BaseNetworkSelect from "./BaseNetworkSelect.js";
import SelectedNetworks from "./SelectedNetworks.js";
import useNetworkItems from "./useNetworkItems.js";

// Conditional type for field value based on multiselect mode
type NetworkSelectValue<MultiSelect extends boolean> = MultiSelect extends true
  ? NetworkId[] | undefined
  : NetworkId | undefined;

interface NetworkSelectProps<
  TFieldValues extends FieldValues,
  TName extends FieldPathByValue<TFieldValues, NetworkSelectValue<MultiSelect>>,
  MultiSelect extends boolean,
> extends React.ComponentProps<typeof BaseNetworkSelect<MultiSelect>> {
  control: Control<TFieldValues>;
  name: TName;
}

function NetworkSelect<
  TFieldValues extends FieldValues,
  MultiSelect extends boolean = false,
  TName extends FieldPathByValue<
    TFieldValues,
    NetworkSelectValue<MultiSelect>
  > = FieldPathByValue<TFieldValues, NetworkSelectValue<MultiSelect>>,
>({
  control,
  name,
  networkInfos,
  app,
  chainInfo,
  sort,
  RenderComponent = SelectedNetworks,
  placeholder = "All networks",
  ...props
}: NetworkSelectProps<TFieldValues, TName, MultiSelect>) {
  const items = useNetworkItems({
    networkInfos,
    chainInfo,
    app,
    sort,
  });

  return (
    <SelectField
      control={control}
      name={name}
      items={items}
      placeholder={placeholder}
      renderSelectedValue={(values) => (
        <RenderComponent
          values={values}
          networkInfos={networkInfos}
          chainInfo={chainInfo}
          placeholder={placeholder}
        />
      )}
      {...props}
    />
  );
}

export default NetworkSelect;
